import { LogLevel, SDKLogger } from './types'

const LEVEL_PRIORITY: Record<Exclude<LogLevel, 'silent'>, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
}

function shouldLog(
  configured: LogLevel,
  level: Exclude<LogLevel, 'silent'>,
): boolean {
  if (configured === 'silent') return false
  return LEVEL_PRIORITY[level] <= LEVEL_PRIORITY[configured]
}

function isErrorLike(value: unknown): value is Error {
  return (
    value instanceof Error ||
    (typeof value === 'object' &&
      value !== null &&
      'message' in value &&
      'stack' in value)
  )
}

function formatArgs(
  level: Exclude<LogLevel, 'silent'>,
  bindings: Record<string, unknown> | undefined,
  args: any[],
) {
  const prefix = bindings
    ? `[sdk=${bindings.sdk ?? 'sdk'}${
        bindings.component ? `:${bindings.component}` : ''
      }]`
    : ''

  if (args.length === 0) return [prefix]

  const [first, ...rest] = args

  // Handle common pattern: logger.error(err, "msg", meta)
  if (isErrorLike(first)) {
    return [
      prefix,
      first,
      ...rest,
      bindings ? { ...bindings } : undefined,
    ].filter(Boolean)
  }

  return [prefix, ...args, bindings ? { ...bindings } : undefined].filter(
    Boolean,
  )
}

/** Create an SDK logger backed by console methods with level filtering. */
export function consoleLogger(
  level: LogLevel = 'info',
  bindings?: Record<string, unknown>,
): SDKLogger {
  const make =
    (method: Exclude<LogLevel, 'silent'>) =>
    (...args: any[]) => {
      if (!shouldLog(level, method)) return

      const formatted = formatArgs(method, bindings, args)

      switch (method) {
        case 'error':
          console.error(...formatted)
          break
        case 'warn':
          console.warn(...formatted)
          break
        case 'info':
          console.info(...formatted)
          break
        case 'debug':
          console.debug(...formatted)
          break
        case 'trace':
          console.trace(...formatted)
          break
      }
    }

  return {
    error: make('error'),
    warn: make('warn'),
    info: make('info'),
    debug: make('debug'),
    trace: make('trace'),

    child(childBindings: Record<string, unknown>) {
      return consoleLogger(level, {
        ...bindings,
        ...childBindings,
      })
    },
  }
}
